import type { RunOverviewEntry } from './types'

/** `12 trials/min` — the throughput suffix shared by the row and its breakdown line. */
export function throughputText(throughputPerMin: number): string {
  return `${throughputPerMin} trials/min`
}

/**
 * The second line under a run row — the live breakdown. A healthy run reads
 * `running 9 · queued 60 · 12 trials/min` (counts then pace). A stalled run reads
 * the loud form `3 blocked · RAM cap · 0 trials/min`, leading with the blocker so
 * the eye lands on why it is not moving (design spec "Calm by default, loud on
 * problems").
 */
export function breakdownText(run: RunOverviewEntry): string {
  if (run.stalled) {
    const parts: string[] = []
    if (run.blocked > 0) {
      parts.push(`${run.blocked} blocked`)
    }
    if (run.blockReason) {
      parts.push(run.blockReason)
    }
    parts.push(throughputText(run.throughputPerMin))
    return parts.join(' · ')
  }
  return `running ${run.counts.running} · queued ${run.counts.queued} · ${throughputText(run.throughputPerMin)}`
}

/**
 * The ETA cell text.
 *
 * - Moving run (etaText set, not stalled): '~14m left'.
 * - Genuinely stalled (stalled === true): 'stalled' - outstanding work, zero
 *   completions in the 5-min window.
 * - Finished run (done === total, etaText === null, stalled === false): 'done'.
 * - Early run with no ETA yet (done !== total, etaText === null, not stalled): '\u2014' (U+2014 em dash)
 *   (neutral dash - never the false alarm of 'stalled').
 */
export function etaText(run: RunOverviewEntry): string {
  if (run.stalled) {
    return 'stalled'
  }
  if (run.etaText === null) {
    return run.done === run.total ? 'done' : '\u2014'
  }
  return `${run.etaText} left`
}

/** Done-fraction in [0,1] for the progress bar; guards a zero/empty total. */
export function progressFraction(done: number, total: number): number {
  if (total <= 0) {
    return 0
  }
  return Math.min(1, Math.max(0, done / total))
}

/**
 * Render an epoch-ms snapshot timestamp as an honest `updated Ns ago` string,
 * relative to `now`. Story C drives the staleness thresholds; Story B uses this
 * for the header freshness indicator. Negative/zero deltas read `just now`.
 */
export function updatedAgo(snapshotTs: number, now: number = Date.now()): string {
  const deltaSec = Math.max(0, Math.round((now - snapshotTs) / 1000))
  if (deltaSec === 0) {
    return 'updated just now'
  }
  if (deltaSec < 60) {
    return `updated ${deltaSec}s ago`
  }
  const min = Math.floor(deltaSec / 60)
  const sec = deltaSec % 60
  return sec === 0 ? `updated ${min}m ago` : `updated ${min}m ${sec}s ago`
}

/**
 * Render a millisecond duration as a terse `6m04s` / `44s` / `2h 5m` string for
 * the drawer's per-stage dwell column. Sub-minute reads as whole seconds;
 * minute-plus pads seconds to two digits so a column of figures lines up under
 * tabular numerals. A negative/NaN input reads `0s` rather than a broken value.
 */
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) {
    return '0s'
  }
  const totalSec = Math.round(ms / 1000)
  if (totalSec < 60) {
    return `${totalSec}s`
  }
  const totalMin = Math.floor(totalSec / 60)
  const sec = totalSec % 60
  if (totalMin < 60) {
    return `${totalMin}m${String(sec).padStart(2, '0')}s`
  }
  const hours = Math.floor(totalMin / 60)
  const min = totalMin % 60
  return min === 0 ? `${hours}h` : `${hours}h ${min}m`
}

/**
 * Render an epoch-ms timestamp as an ISO-8601 instant for the drawer's timestamp
 * rows, or an em dash when the stage was never reached (a null timestamp). Kept
 * absolute (not relative) so an operator can correlate it against logs.
 */
export function formatInstant(ts: number | null): string {
  return ts === null ? '—' : new Date(ts).toISOString()
}

/**
 * A compact absolute clock (`HH:MM:SS`, UTC) for the dense list/board rows, or an
 * em dash for a stage that was never reached. The full ISO instant is kept for a
 * row's `title` tooltip ({@link formatInstant}); this is the at-a-glance form so
 * a row can show *when* a trial/run happened without the verbose date.
 */
export function formatClock(ts: number | null): string {
  return ts === null ? '—' : new Date(ts).toISOString().slice(11, 19)
}

/** The human-readable when+duration a list/board row surfaces. */
export interface RowTiming {
  /** Full ISO-8601 instant for the row's `title` tooltip, or an em dash. */
  whenIso: string
  /** Compact `HH:MM:SS` clock shown inline, or an em dash. */
  whenClock: string
  /** How long the work took / has taken, e.g. `6m04s` ({@link formatDuration}). */
  duration: string
}

/**
 * The when+duration for one trial row, derived purely from the four lifecycle
 * stamps — no backend field is invented. `when` is when the trial actually began
 * running (`startedAt`), falling back to when it was enqueued (`createdAt`) for a
 * trial that has not started. `duration` is the elapsed wall time from enqueue to
 * terminal (`completedAt`), or to `now` while the trial is still in flight, so a
 * running/queued row reads how long it has been going and a finished row reads
 * how long it took.
 */
export function trialRowTiming(
  trial: { createdAt: number; leasedAt: number | null; startedAt: number | null; completedAt: number | null },
  now: number = Date.now()
): RowTiming {
  const whenTs = trial.startedAt ?? trial.createdAt
  const end = trial.completedAt ?? now
  return {
    whenIso: formatInstant(whenTs),
    whenClock: formatClock(whenTs),
    duration: formatDuration(gap(trial.createdAt, end)),
  }
}

/**
 * The when+duration for one run row. `when` is when the run was first seen
 * (`firstSeenAt`); `duration` is the span from first-seen to its last recorded
 * activity (`lastActivityAt`), the human read on how long the run has spanned.
 */
export function runRowTiming(
  run: { firstSeenAt: number; lastActivityAt: number },
  _now: number = Date.now()
): RowTiming {
  return {
    whenIso: formatInstant(run.firstSeenAt),
    whenClock: formatClock(run.firstSeenAt),
    duration: formatDuration(gap(run.firstSeenAt, run.lastActivityAt)),
  }
}

/**
 * Tolerant phase-token → human sub-label map (Story E).
 *
 * The daemon retains the LATEST raw phase token a trial emitted and the UI
 * sub-labels the Running lane (and the drawer) with it. The tokens are
 * free-form strings sourced from the guest shim (`trial_start`,
 * `bundle_write_done`, harness phases, …) — NOT a closed enum (dry-run §Area 5).
 * So this map is deliberately tolerant: a recognized token gets a terse,
 * legible label, and ANY unrecognized token degrades to its raw form rather
 * than crashing or rendering blank. New backend phases therefore surface
 * legibly the day they ship, before this map learns about them.
 */
const PHASE_SUB_LABELS: Readonly<Record<string, string>> = {
  // --- shim lifecycle (trial-shim.ts) ---
  trial_start: 'starting',
  trial_error: 'error',
  trial_no_evidence: 'no evidence',
  bundle_write_start: 'bundle write',
  bundle_write_done: 'bundle written',
  bundle_write_failed: 'bundle failed',
  // --- VM boot (vm-lifecycle.ts) ---
  vmboot: 'vm boot',
  // --- harness runner (harness-runner.ts) ---
  harness_start: 'harness',
  harness_exit: 'harness done',
  // --- guest harness phases (obvious/evals/harness) ---
  pg_start: 'postgres init',
  agent_turn: 'agent turn',
  judge: 'judging',
  // --- compose executor arm wrappers (compose-executor.ts) ---
  'arm-start': 'arm start',
  'arm-end': 'arm end',
}

/**
 * Resolve a raw phase token to its display sub-label. A known token maps to a
 * terse label; an unknown (or whitespace-trimmed-empty) token returns the raw
 * token unchanged, so the Running lane never blanks on a phase the UI has not
 * been taught (design spec "The latest-phase decision"; dry-run §Area 5).
 */
export function phaseSubLabel(phase: string): string {
  const known = PHASE_SUB_LABELS[phase]
  if (known !== undefined) {
    return known
  }
  const trimmed = phase.trim()
  return trimmed.length > 0 ? trimmed : phase
}

/**
 * The Running-lane / drawer sub-phase line: `<label> <dwell>` (e.g.
 * `harness 1m12s`), or just the label when no dwell is known. Returns null when
 * no phase is retained (after a restart, or a non-running trial) so the caller
 * falls back to plain status rather than rendering an empty token.
 */
export function phaseDwellText(latestPhase: string | null, phaseDwellMs: number | null): string | null {
  if (latestPhase === null) {
    return null
  }
  const label = phaseSubLabel(latestPhase)
  return phaseDwellMs === null ? label : `${label} ${formatDuration(phaseDwellMs)}`
}

/** One trial stage and how long the trial dwelled in it (ms), or null if not reached. */
export interface StageDwell {
  label: string
  dwellMs: number | null
}

/**
 * Per-stage dwell from the four trial timestamps, the bottleneck signal the
 * drawer shows (design spec §"Single-trial drawer" / §"Dwell time"). Each stage's
 * dwell is the gap to the next timestamp; the final, still-open stage runs to
 * `now`. A stage whose start timestamp is null was never reached, so its dwell
 * is null and renders as an em dash rather than a misleading zero.
 */
export function stageDwells(
  trial: { createdAt: number; leasedAt: number | null; startedAt: number | null; completedAt: number | null },
  now: number = Date.now()
): StageDwell[] {
  const { createdAt, leasedAt, startedAt, completedAt } = trial
  return [
    { label: 'queued', dwellMs: gap(createdAt, leasedAt ?? startedAt ?? completedAt ?? now) },
    { label: 'leased', dwellMs: leasedAt === null ? null : gap(leasedAt, startedAt ?? completedAt ?? now) },
    { label: 'running', dwellMs: startedAt === null ? null : gap(startedAt, completedAt ?? now) },
  ]
}

/** Non-negative gap between two epoch-ms instants. */
function gap(from: number, to: number): number {
  return Math.max(0, to - from)
}

/**
 * After this many seconds without a successful poll, the freshness indicator
 * stops saying "updated Ns ago" and admits the connection is gone. Tuned to a few
 * missed polls at the relaxed 2–5s cadence (design spec "Liveness via cached
 * polling"): long enough that one slow tick does not cry wolf, short enough that
 * a real outage surfaces quickly.
 */
export const DISCONNECTED_AFTER_SEC = 12

/** The honest freshness line, given the last successful poll and whether polling is failing. */
export interface Freshness {
  /** The text to render — `updated Ns ago` or `disconnected — retrying`. */
  text: string
  /** True once we have crossed into the disconnected state, for styling. */
  disconnected: boolean
}

/**
 * Compute the freshness line for the header. While polls land it reads
 * `updated Ns ago` from the last success; once polling has failed AND the last
 * success is older than {@link DISCONNECTED_AFTER_SEC}, it ages honestly to
 * `disconnected — retrying` so an operator never trusts stale data as live. Before
 * the first poll completes it reads `updating…`.
 */
export function freshness(
  lastSuccessAt: number | null,
  failing: boolean,
  now: number = Date.now(),
  disconnectedAfterSec: number = DISCONNECTED_AFTER_SEC
): Freshness {
  if (lastSuccessAt === null) {
    return failing
      ? { text: 'disconnected — retrying', disconnected: true }
      : { text: 'updating…', disconnected: false }
  }
  const ageSec = Math.max(0, Math.round((now - lastSuccessAt) / 1000))
  if (failing && ageSec >= disconnectedAfterSec) {
    return { text: 'disconnected — retrying', disconnected: true }
  }
  return { text: updatedAgo(lastSuccessAt, now), disconnected: false }
}
